import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiBell,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiPhoneCall,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiSlash,
  FiTag,
  FiUser,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, PageHeader } from '../components/common/index.js';
import { notAvailableApi } from '../api/notAvailableApi';

const normalizeParts = (payload) => {
  const items = Array.isArray(payload)
    ? payload
    : payload?.demand ?? payload?.parts ?? payload?.results ?? [];

  return items.map((item) => ({
    ...item,
    inquiries: (item.inquiries ?? []).map((inquiry) => ({
      ...inquiry,
      car: inquiry.car || item.car,
    })),
  }));
};

const normalizeRestocks = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.restocks ?? payload?.results ?? payload?.notices ?? [];
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const errorMessage = (error, fallback) => {
  const data = error?.response?.data ?? error;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (Array.isArray(data)) return data.join(', ');
  if (data.detail || data.message || data.error) return data.detail || data.message || data.error;
  return Object.entries(data)
    .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(' | ') || fallback;
};

const countWaiting = (parts) =>
  parts.reduce((sum, item) => sum + Number(item.waiting || item.inquiries?.length || 0), 0);

const restockTotal = (restocks, key) =>
  restocks.reduce((sum, item) => sum + Number(item?.progress?.[key] ?? item?.[key] ?? 0), 0);

const TABS = {
  NOT_AVAILABLE: 'not_available',
  RESTOCK: 'restock',
};

// Where the restocked part comes from. 'stock' is the outside-parts shelf —
// same three sources a part can be quoted from anywhere else in the system.
const SOURCE_OPTIONS = [
  { value: 'cap', label: 'CAP' },
  { value: 'osm', label: 'OSM' },
  { value: 'stock', label: 'Outside Parts' },
];

const sourceLabel = (value) =>
  SOURCE_OPTIONS.find((option) => option.value === String(value || '').toLowerCase())?.label || String(value || '').toUpperCase();

const Modal = ({ title, children, onClose }) => {
  useEffect(() => {
    const onEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close dialog" />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in-up">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <svg className="h-8 w-8 animate-spin text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

const StatCard = ({ icon: Icon, label, value, helper, tone = 'violet' }) => {
  const tones = {
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    blue: 'bg-blue-50 text-blue-600',
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-0.5 text-2xl font-bold leading-none text-slate-900">{value}</p>
        </div>
      </div>
      {helper && <p className="mt-3 text-xs text-slate-500">{helper}</p>}
    </div>
  );
};

const TabButton = ({ active, icon: Icon, label, count, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-bold transition-all ${
      active
        ? 'bg-violet-600 text-white shadow-sm shadow-violet-200'
        : 'text-slate-500 hover:bg-white hover:text-slate-800'
    }`}
  >
    <Icon className="h-4 w-4 shrink-0" />
    <span className="truncate">{label}</span>
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
      {count}
    </span>
  </button>
);

const EmptyState = ({ hasSearch }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
      <FiCheckCircle className="h-7 w-7 text-emerald-500" />
    </div>
    <p className="font-semibold text-slate-800">{hasSearch ? 'No matching parts' : 'No unavailable parts right now'}</p>
    <p className="mt-1 max-w-md text-sm text-slate-500">
      {hasSearch ? 'Try a different part, customer, car, or salesperson search.' : 'Parts that customers are waiting for will appear here.'}
    </p>
  </div>
);

const InquiryTable = ({ inquiries }) => (
  <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-50">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Customer</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Salesperson</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Car</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Stage</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Marked</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {inquiries.map((item) => (
          <tr key={`${item.inquiry_id}-${item.part_id}`} className="align-top">
            <td className="px-4 py-3">
              <p className="font-semibold text-slate-800">{item.customer_name || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">{item.customer_phone || '-'}</p>
            </td>
            <td className="px-4 py-3 text-slate-700">{item.salesperson_name || '-'}</td>
            <td className="px-4 py-3 text-slate-700">{item.car || '-'}</td>
            <td className="px-4 py-3">
              <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-600">
                {item.stage || '-'}
              </span>
            </td>
            <td className="whitespace-nowrap px-4 py-3 text-slate-500">{formatDate(item.marked_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DemandCard = ({ item, onRestock }) => {
  const inquiries = item.inquiries ?? [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <FiSlash className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-900">{item.part || 'Unnamed part'}</h3>
            <p className="mt-1 text-sm text-slate-500">
              {Number(item.waiting || inquiries.length)} customers waiting
            </p>
            {item.car && <p className="mt-1 text-xs font-medium text-slate-400">{item.car}</p>}
          </div>
        </div>
        <Button size="sm" variant="primary" onClick={() => onRestock(item)} className="gap-2 sm:shrink-0">
          <FiSend className="h-3.5 w-3.5" />
          Restock
        </Button>
      </div>

      {inquiries.length > 0 && <InquiryTable inquiries={inquiries} />}
    </div>
  );
};

const RestockProgress = ({ restocks, loading, onRepriceRestock }) => {
  if (loading) return <Spinner />;
  if (restocks.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center">
        <FiBell className="mx-auto mb-3 h-7 w-7 text-slate-300" />
        <p className="font-semibold text-slate-700">No restock notices yet</p>
        <p className="mt-1 text-sm text-slate-500">Sent notices and callback progress will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {restocks.map((item, index) => {
        const progress = item.progress ?? item;
        const total = Number(progress.total ?? 0);
        const done = Number(progress.contacted ?? 0) + Number(progress.converted ?? 0) + Number(progress.not_interested ?? 0);
        const completion = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
        const key = item.id ?? item.restock_id ?? `${item.part || 'restock'}-${item.created_at || index}`;
        const price = formatPrice(item.price ?? item.restock_price);
        const source = item.source ?? item.restock_source;

        return (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="truncate font-bold text-slate-900">{item.part || item.part_name || 'Restock notice'}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {price ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                        {price}
                      </span>
                      {source && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700">
                          <FiTag className="h-3.5 w-3.5" />
                          {sourceLabel(source)}
                        </span>
                      )}
                      {item.price_updated_by && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                          Priced by {item.price_updated_by}
                          {item.price_updated_role === 'admin' ? ' (Admin)' : ''}
                        </span>
                      )}
                      {onRepriceRestock && (
                        <button
                          type="button"
                          onClick={() => onRepriceRestock(item)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-violet-300 hover:text-violet-700"
                          title="Correct this price"
                        >
                          <FiEdit2 className="h-3.5 w-3.5" />
                          Change price
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                        <FiClock className="h-3.5 w-3.5" />
                        Awaiting supervisor price
                      </span>
                      {onRepriceRestock && (
                        <button
                          type="button"
                          onClick={() => onRepriceRestock(item)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-violet-300 hover:text-violet-700"
                        >
                          <FiEdit2 className="h-3.5 w-3.5" />
                          Set price
                        </button>
                      )}
                    </>
                  )}
                </div>
                {/* Who said what, in order — a wrong price needs to be traceable
                    to whoever typed it, on either side. */}
                <PriceTrail history={item.price_history} />
                {/* One notice per car: without the label, the same part shows
                    as several identical-looking rows. */}
                {Array.isArray(item.cars) && item.cars.length > 0 && (
                  <p className="mt-1 text-xs font-medium text-slate-500">{item.cars.join(' · ')}</p>
                )}
                {item.note && <p className="mt-1 text-sm text-slate-500">{item.note}</p>}
                <p className="mt-1 text-xs text-slate-400">{formatDate(item.created_at || item.sent_at || item.marked_at)}</p>
              </div>
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700">
                <FiClock className="h-3.5 w-3.5" />
                {completion}% done
              </span>
            </div>

            <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-violet-600 transition-all" style={{ width: `${completion}%` }} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
              <ProgressPill label="Total" value={progress.total} />
              <ProgressPill label="To call" value={progress.to_call} />
              <ProgressPill label="Contacted" value={progress.contacted} />
              <ProgressPill label="Converted" value={progress.converted} />
              <ProgressPill label="Not interested" value={progress.not_interested} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// "Admin (Office Desk) ₹90,000 → Mukthar ₹84,500". Either side can price a
// restock and either side can get it wrong, so the order and the names are the
// point. Nothing renders until there is an actual change to show.
const PriceTrail = ({ history }) => {
  const trail = Array.isArray(history) ? history : [];
  if (trail.length < 2) return null;

  const who = (e) =>
    (e.role === 'admin' ? `Admin${e.by ? ` (${e.by})` : ''}` : e.by || 'Supervisor');
  const amount = (e) => (e.price == null ? '—' : formatPrice(e.price));

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-slate-600">
      {trail.map((e, i) => (
        <span key={`${e.at}-${i}`} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-slate-300">→</span>}
          <span
            className={`rounded px-1.5 py-0.5 font-semibold ${
              e.role === 'admin' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'
            }`}
            title={e.at || ''}
          >
            {who(e)} {amount(e)}
          </span>
        </span>
      ))}
    </div>
  );
};

const ProgressPill = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-0.5 text-lg font-bold leading-none text-slate-800">{Number(value ?? 0)}</p>
  </div>
);

// Announce, with the price when you know it. Leave price blank and a supervisor
// is pushed to set it (the original behaviour). Fill it in and sales can quote
// straight away — a supervisor can still correct it, and every correction is
// recorded against whoever made it.
const RestockModal = ({ part, submitting, error, onClose, onSubmit }) => {
  const waitingCount = Number(part.waiting || part.inquiries?.length || 0);
  const [price, setPrice] = useState('');
  const [source, setSource] = useState('');

  const priced = String(price).trim() !== '';
  // Sales cannot quote without knowing where the part comes from, so the
  // backend requires a source with any price — mirror that here.
  const blocked = priced && !source;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (blocked) return;
    const body = { part: part.part, car: part.car || '' };
    if (priced) {
      body.price = Number(price);
      body.source = source;
    }
    onSubmit(body);
  };

  return (
    <Modal title="Send Restock Notice" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Part</p>
          <p className="mt-1 font-bold text-slate-900">{part.part}</p>
          {part.car && <p className="mt-0.5 text-sm font-medium text-slate-600">{part.car}</p>}
          <p className="mt-1 text-sm text-slate-600">{waitingCount} customers waiting</p>
        </div>

        {/* Optional price. Blank keeps the old flow; filled saves a round-trip. */}
        <div className="rounded-xl border border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Price (optional)
            </p>
            {priced && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                Sales can quote immediately
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                type="number" min="1" step="1" value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Leave blank for supervisor"
                className="w-56 rounded-xl border border-slate-200 py-2 pl-7 pr-3 text-sm font-semibold text-slate-800 focus:border-violet-400 focus:outline-none"
              />
            </div>
            {SOURCE_OPTIONS.map((option) => (
              <button
                key={option.value} type="button"
                onClick={() => setSource(source === option.value ? '' : option.value)}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition-colors ${
                  source === option.value
                    ? 'border-violet-400 bg-violet-50 text-violet-700'
                    : 'border-slate-200 text-slate-500 hover:border-violet-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {blocked && (
            <p className="mt-2 text-xs font-semibold text-rose-600">
              Pick where it comes from — sales need to know before quoting.
            </p>
          )}
        </div>

        <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm leading-6 text-violet-900">
          {priced ? (
            <>Sales get this price right away. A <span className="font-bold">supervisor</span> can
            still correct it, and the change is recorded against their name.</>
          ) : (
            <>Without a price this only announces the part is back. A{' '}
            <span className="font-bold">supervisor</span> sets the price and source next —
            they get a notification — and sales quote once it is in.</>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting} disabled={blocked} className="gap-2">
            <FiSend className="h-4 w-4" />
            {priced ? 'Send Notice with Price' : 'Send Notice'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Correct a restock price from the office side. Shows the existing trail first,
// because the usual reason for opening this is that a number looks wrong and
// the next question is always "who put that there".
const RepriceModal = ({ notice, submitting, error, onClose, onSubmit }) => {
  const [price, setPrice] = useState(notice.price != null ? String(notice.price) : '');
  const [source, setSource] = useState(String(notice.source || '').toLowerCase());
  const [note, setNote] = useState(notice.note || '');

  const valid = String(price).trim() !== '' && Number(price) > 0;
  // Sales can't quote without knowing the source, so it's required the first
  // time a price is set — same rule the supervisor path enforces.
  const needsSource = valid && !source;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!valid || needsSource) return;
    onSubmit({ id: notice.id, price: Number(price), source, note });
  };

  return (
    <Modal title="Change restock price" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="font-bold text-slate-900">{notice.part}</p>
          {Array.isArray(notice.cars) && notice.cars.length > 0 && (
            <p className="mt-0.5 text-sm text-slate-600">{notice.cars.join(' · ')}</p>
          )}
          <PriceTrail history={notice.price_history} />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="reprice-amount">
            New price
          </label>
          <div className="relative mt-1.5">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
            <input
              id="reprice-amount" type="number" min="1" step="1" value={price} autoFocus
              onChange={(e) => setPrice(e.target.value)}
              className="w-full rounded-xl border border-slate-200 py-2 pl-7 pr-3 text-sm font-semibold text-slate-800 focus:border-violet-400 focus:outline-none"
            />
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source</p>
          <div className="mt-1.5 flex gap-2">
            {SOURCE_OPTIONS.map((option) => (
              <button
                key={option.value} type="button"
                onClick={() => setSource(source === option.value ? '' : option.value)}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition-colors ${
                  source === option.value
                    ? 'border-violet-400 bg-violet-50 text-violet-700'
                    : 'border-slate-200 text-slate-500 hover:border-violet-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {needsSource && (
            <p className="mt-2 text-xs font-semibold text-rose-600">
              Pick where it comes from — sales need to know before quoting.
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="reprice-note">
            Note (optional)
          </label>
          <input
            id="reprice-note" type="text" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. 5 units arrived, OEM"
            className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-violet-400 focus:outline-none"
          />
        </div>

        <p className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm leading-6 text-violet-900">
          Saved against your name, and every salesperson still calling customers
          about this part is told the new price.
        </p>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={submitting} disabled={!valid || needsSource} className="gap-2">
            <FiEdit2 className="h-4 w-4" />
            Save price
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const NotAvailablePage = () => {
  const [activeTab, setActiveTab] = useState(TABS.NOT_AVAILABLE);
  const [parts, setParts] = useState([]);
  const [restocks, setRestocks] = useState([]);
  const [loadingDemand, setLoadingDemand] = useState(true);
  const [loadingRestocks, setLoadingRestocks] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedPart, setSelectedPart] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // The notice whose price the office is correcting.
  const [repriceTarget, setRepriceTarget] = useState(null);

  const loadDemand = useCallback(async () => {
    setLoadingDemand(true);
    try {
      const response = await notAvailableApi.getDemandList();
      setParts(normalizeParts(response.data));
    } catch (requestError) {
      setError(errorMessage(requestError, 'Failed to load unavailable parts'));
    } finally {
      setLoadingDemand(false);
    }
  }, []);

  const loadRestocks = useCallback(async () => {
    setLoadingRestocks(true);
    try {
      const response = await notAvailableApi.getRestocks();
      setRestocks(normalizeRestocks(response.data));
    } catch (requestError) {
      setError(errorMessage(requestError, 'Failed to load restock progress'));
    } finally {
      setLoadingRestocks(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setError(null);
    await Promise.all([loadDemand(), loadRestocks()]);
  }, [loadDemand, loadRestocks]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filteredParts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return parts;
    return parts.filter((item) => {
      const fields = [
        item.part,
        item.car,
        ...(item.inquiries || []).flatMap((inquiry) => [
          inquiry.customer_name,
          inquiry.customer_phone,
          inquiry.salesperson_name,
          inquiry.car,
          inquiry.stage,
        ]),
      ];
      return fields.some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [parts, search]);

  const handleRestock = async ({ part, car, price, source }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = car ? { part, car } : { part };
      // Price is optional — omitted entirely when blank, which keeps the
      // "supervisor sets it" flow and its notification intact.
      if (price != null && price !== '') {
        payload.price = price;
        payload.source = source;
      }
      const response = await notAvailableApi.restockPart(payload);
      const priced = response?.data?.priced;
      setSuccess(
        priced
          ? `${part} restock sent at ${formatPrice(price)} — sales can quote now.`
          : `${part} restock notice sent — supervisor will set the price.`,
      );
      setSelectedPart(null);
      await loadAll();
    } catch (requestError) {
      setSubmitError(errorMessage(requestError, 'Failed to send restock notice'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReprice = async ({ id, price, source, note }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = { price };
      if (source) body.source = source;
      if (note != null) body.note = note;
      const response = await notAvailableApi.updateRestock(id, body);
      const notified = response?.data?.sales_notified ?? 0;
      setSuccess(
        `Price updated to ${formatPrice(price)}`
        + (notified ? ` — ${notified} salesperson(s) told.` : '.'),
      );
      setRepriceTarget(null);
      await loadAll();
    } catch (requestError) {
      setSubmitError(errorMessage(requestError, 'Failed to update the price'));
    } finally {
      setSubmitting(false);
    }
  };

  const totalWaiting = countWaiting(parts);
  const totalToCall = restockTotal(restocks, 'to_call');
  const totalConverted = restockTotal(restocks, 'converted');

  return (
    <MainLayout>
      <div className="space-y-6">
        <PageHeader purpose="Parts customers are waiting for. Restock one and the right salespeople get a callback task.">
          <Button variant="secondary" onClick={loadAll} disabled={loadingDemand || loadingRestocks} className="gap-2">
            <FiRefreshCw className={`h-4 w-4 ${(loadingDemand || loadingRestocks) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </PageHeader>

        {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

        <div className="rounded-xl bg-slate-200/70 p-1" role="tablist" aria-label="Not available sections">
          <div className="grid grid-cols-2 gap-1">
            <TabButton
              active={activeTab === TABS.NOT_AVAILABLE}
              icon={FiSlash}
              label="Not Available"
              count={parts.length}
              onClick={() => setActiveTab(TABS.NOT_AVAILABLE)}
            />
            <TabButton
              active={activeTab === TABS.RESTOCK}
              icon={FiBell}
              label="ReStock"
              count={restocks.length}
              onClick={() => setActiveTab(TABS.RESTOCK)}
            />
          </div>
        </div>

        {activeTab === TABS.NOT_AVAILABLE && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard icon={FiSlash} label="Unavailable parts" value={parts.length} helper="Grouped by part name" tone="amber" />
              <StatCard icon={FiUser} label="Customers waiting" value={totalWaiting} helper="Open not-available demand" tone="violet" />
            </div>

            <section className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Demand List</h2>
                  <p className="text-sm text-slate-500">Biggest waiting list appears first.</p>
                </div>
                <div className="relative w-full sm:max-w-sm">
                  <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search demand..."
                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
              </div>

              {loadingDemand ? (
                <Spinner />
              ) : filteredParts.length === 0 ? (
                <EmptyState hasSearch={Boolean(search.trim())} />
              ) : (
                <div className="space-y-4">
                  {filteredParts.map((item, index) => (
                    <DemandCard key={`${item.part || 'part'}-${index}`} item={item} onRestock={setSelectedPart} />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {activeTab === TABS.RESTOCK && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <StatCard icon={FiPhoneCall} label="Calls pending" value={totalToCall} helper="Across sent restocks" tone="blue" />
              <StatCard icon={FiCheckCircle} label="Converted" value={totalConverted} helper="Reported follow-ups" tone="emerald" />
            </div>

            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">ReStock Progress</h2>
                <p className="text-sm text-slate-500">Live callback status for notices already sent.</p>
              </div>
              <RestockProgress
                restocks={restocks}
                loading={loadingRestocks}
                onRepriceRestock={setRepriceTarget}
              />
            </section>
          </div>
        )}
      </div>

      {selectedPart && (
        <RestockModal
          part={selectedPart}
          submitting={submitting}
          error={submitError}
          onClose={() => {
            if (!submitting) {
              setSelectedPart(null);
              setSubmitError(null);
            }
          }}
          onSubmit={handleRestock}
        />
      )}

      {repriceTarget && (
        <RepriceModal
          notice={repriceTarget}
          submitting={submitting}
          error={submitError}
          onClose={() => {
            if (!submitting) {
              setRepriceTarget(null);
              setSubmitError(null);
            }
          }}
          onSubmit={handleReprice}
        />
      )}
    </MainLayout>
  );
};

export default NotAvailablePage;
